<template>
    <div>
        <div style="display:grid; grid-template-columns: 3fr 9fr;">
            <div>
                <fe-remote-combo
                    label="School Year"
                    class="mt-2 mr-2"
                    style="margin-bottom: 28px;"
                    :url="$models.getUrl('schoolYear', 'read')"
                    rootProperty="years"
                    v-model="schoolYear"
                    hide-details
                />
            </div>

            <div class="d-flex">
                <v-flex xs12 v-show="searchBy == 'name'" class="ml-3">
                    <fe-label style="margin-top: 8px;">Student Name</fe-label>
                    <v-text-field
                        placeholder="Type a student name (3 characters minimum)"
                        v-model="searchText"
                        solo flat dense
                        hide-details
                    />
                </v-flex>

                <v-flex xs6 v-show="searchBy == 'school'" class="ml-3">
                    <fe-remote-combo
                        label="School"
                        class="mt-2"
                        :url="$models.getUrl('school', 'read')+'&school_year_id='+schoolYear.id"
                        rootProperty="schools"
                        v-model="school"
                        hide-details
                    />
                </v-flex>

                <v-flex xs6 v-show="searchBy == 'school'" class="ml-3">
                    <fe-remote-combo
                        label="Grade"
                        class="mt-2 ml-2"
                        :url="$models.getUrl('grade', 'read')+'&school_year_id='+schoolYear.id"
                        rootProperty="grades"
                        v-model="grade"
                        hide-details
                    />
                </v-flex>

                <v-flex xs6 v-show="searchBy == 'course'" class="ml-3">
                    <fe-remote-combo
                        label="Teacher"
                        class="mt-2"
                        :url="userCourseHistory"
                        rootProperty="courses"
                        itemText="user_full_name"
                        itemValue="user_id"
                        v-model="teacher"
                        hide-details
                    />
                </v-flex>

                <v-flex xs6 v-show="searchBy == 'course'" class="ml-3">
                    <fe-remote-combo
                        label="Course"
                        class="mt-2 ml-2"
                        :url="userCourses"
                        rootProperty="courses"
                        itemText="course_name"
                        itemValue="course_id"
                        v-model="course"
                        :disabled="!teacher"
                        hide-details
                    />
                </v-flex>

                <v-flex xs6 v-show="searchBy == 'intervention'" class="ml-3">
                    <fe-remote-combo
                        label="Intervention"
                        class="mt-2"
                        url="crud.php?action=get&property=Category"
                        rootProperty="category"
                        v-model="category"
                        multiple
                        @change="loadDataByCategory"
                        hide-details
                    />
                </v-flex>

                <v-flex xs6 v-show="searchBy == 'tag'" class="ml-3">
                    <fe-remote-combo
                        label="Tag"
                        class="mt-2"
                        :url="$models.getUrl('tag', 'read')"
                        rootProperty="tags"
                        v-model="tag"
                        hide-details
                    />
                </v-flex>

            </div>

            <banner v-if="maximumStudentsAllowed && currentCount+selectedCount > maximumStudentsAllowed" style="grid-column: span 2;margin-bottom: 16px;" usage="warning" :text="`You've exceeded the limit of ${maximumStudentsAllowed} students.`" />

            <div>
                <div
                    @click="searchBy='name'"
                    class="subheading mr-2 student-search-option"
                    :class="[{'selected-student': searchBy == 'name'}]"
                >
                    By Name
                </div>

                <div
                    @click="searchBy='school'"
                    class="subheading mr-2 student-search-option"
                    :class="[{'selected-student': searchBy == 'school'}]"
                >
                    By School/Grade
                </div>

                <div
                    @click="searchBy='course'"
                    class="subheading mr-2 student-search-option"
                    :class="[{'selected-student': searchBy == 'course'}]"
                >
                    By Teacher/Course
                </div>

                <div
                    @click="searchBy='intervention'"
                    class="subheading mr-2 student-search-option"
                    :class="[{'selected-student': searchBy == 'intervention'}]"
                >
                    By Intervention
                </div>

                <div
                    @click="searchBy='tag'"
                    class="subheading mr-2 student-search-option"
                    :class="[{'selected-student': searchBy == 'tag'}]"
                >
                    By Tag
                </div>
            </div>

            <div>
                <v-flex xs12 class="ml-3">
                    <div class="mb-2" v-if="selectedCount > 0">{{ selectedCount }} Student{{ selectedCount > 1 ? 's' : null}} Selected</div>
                    <FeGrid
                        ref="grid"
                        :showToolbar="false"
                        :columnDefs="columnDefs"
                        :rowData="items"
                        @rowSelected="doSelect"
                        style="height: 400px;"
                        displayDensity="medium"
                        :bordered="false"
                    >
                    </FeGrid>
                </v-flex>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapState} from 'vuex'
    import Banner from '@/components/common/Banner'

    export default {
        name: 'StudentSearch',
        components: {
            Banner,
        },
        props: {
            value: {},
            /** maximum number of students allowed */
            maximumStudentsAllowed: {
                type: Number,
                default: null,
            },
            /** current number of students selected */
            currentCount: {
                type: Number,
                default: null,
            },
            searchYear: {}
        },

        data() {
            return {
                selectedCount: 0,
                school: null,
                grade: null,
                schoolYear: {},
                tag: null,
                course: null,
                teacher: null,
                searchBy: 'name',
                searchText: null,
                items: [],
                category: [],
            }
        },

        computed: {
            ...mapState('global', ['currentYear']),
            userCourseHistory() {
                return this.$models.getUrl('courseHistory', 'read') + '&property=user_id&school_year_id=' + this.schoolYear.id
            },
            userCourses() {
                if (!this.teacher) return
                return this.$models.getUrl('courseHistory', 'read') + '&property=course_id&school_year_id=' + this.schoolYear.id + '&user_id=' + this.teacher.user_id
            },
            columnDefs() {
                return (this.searchBy == 'course')
                    ? this.byCourseColumnDefs
                    : (this.searchBy == 'intervention')
                    ? this.byCategoryColumnDefs
                    : this.defaultColumnDefs
            },
            byCourseColumnDefs() {
                return [
                    this.$grid.checkColumn(), {
                        headerName: 'Student',
                        field: 'student_full_name',
                        width: 300
                    }, {
                        headerName: 'Teacher',
                        field: 'user_full_name'
                    }, {
                        headerName: 'Course',
                        field: 'course_name'
                    }, {
                        headerName: 'Period',
                        field: 'period'
                    }
                ]
            },
            defaultColumnDefs() {
                return [
                    this.$grid.checkColumn(), {
                        headerName: 'Student',
                        field: 'student_full_name',
                        width: 300
                    }, {
                        headerName: 'School',
                        field: 'school_name'
                    }, {
                        headerName: 'Grade Level',
                        field: 'grade_desc'
                    }
                ]
            },
            byCategoryColumnDefs() {
                return [
                    this.$grid.checkColumn(), {
                        headerName: 'Student Name',
                        field: 'student_full_name',
                        minWidth: 200,
                        colId: 'studentFullName',
                        sortable: true,
                    }, {
                        headerName: 'Intervention Name',
                        field: 'intervention_plan_name',
                        colId: 'interventionPlanName',
                        minWidth: 200,
                    }, {
                        headerName: 'Category',
                        field: 'category_name',
                        colId: 'categoryName',
                        minWidth: 100,
                    }, {
                        headerName: 'Tier',
                        field: 'intervention_level_name',
                        colId: 'interventionLevelName',
                        minWidth: 200,
                    }, {
                        headerName: 'Strategy',
                        field: 'intervention_type_name',
                        colId: 'interventionTypeName',
                        minWidth: 200,
                    }, {
                        headerName: 'School',
                        field: 'school_name',
                        colId: 'schoolName',
                        minWidth: 200,
                    }, {
                        headerName: 'Grade Level',
                        field: 'grade_desc',
                        colId: 'gradeDesc',
                        minWidth: 100,
                    },
                ]
            },
        },

        watch: {
            schoolYear() {
                this.resetFields()
            },
            searchBy(v) {
                this.items = []
                this.$refs.grid.gridApi.forEachNode((node) => {
                    node.setSelected(false)
                })
                this.$emit('input', [])

                this.resetFields()
            },
            searchText(v) {
                let me = this
                if (v && v.length >= 3) {
                    let s = '&name=' + this.searchText
                    this.$_.debounce(function () {
                        me.loadData(s)
                    }, 500)()
                }
            },
            tag(v) {
                if (v) {
                    let s = '&tag_id=' + v.id
                    this.loadDataByTag(s)
                }
            },
            course(v) {
                if (!v || !this.teacher) return

                let s = '&user_id=' + this.teacher.user_id + '&course_id=' + v.course_id
                this.loadData(s)
            },
            grade(v) {
                if (!v || !this.school) return

                let s = '&school_id=' + this.school.id + '&grade_id=' + v.id
                this.loadData(s)
            }
        },

        mounted() {
            if (this.searchYear) {
                this.schoolYear = {...this.searchYear}
            } else {
                this.schoolYear = {...this.currentYear}
            }
        },

        methods: {
            resetFields() {
                this.searchText = null
                this.school = null
                this.grade = null
                this.teacher = null
                this.course = null
                this.tag = null
                this.category = null
            },
            doSelect() {
                this.selectedCount = this.$refs.grid.gridApi.getSelectedRows().length
                this.$emit('input', this.$refs.grid.gridApi.getSelectedRows())
            },
            loadData(str) {
                if (!str)  return

                if (this.searchBy == 'course')
                    this.loadFromStudentCourseHistory(str)
                else
                    this.loadFromStudentGradeHistory(str)
            },
            loadFromStudentCourseHistory (str) {
                this.$setLoading(true)
                this.$axios.get(this.$models.getUrl('courseHistory', 'read') + '&' + str + '&school_year_id=' + this.schoolYear.id)
                    .then(response => {
                        this.items = this.$ecResponse(response).courses
                    })
                    .finally(() => {
                        this.$setLoading(false)
                    })
            },
            loadFromStudentGradeHistory (str) {
                this.$setLoading(true)
                this.$axios.get(this.$models.getUrl('studentGradeHistory', 'read') + '&' + str + '&school_year_id=' + this.schoolYear.id)
                    .then(response => {
                        this.items = this.$ecResponse(response)
                    })
                    .finally(() => {
                        this.$setLoading(false)
                    })
            },
            loadDataByTag(str) {
                if (!str) return

                this.$setLoading(true)
                this.$axios.get(this.$models.getUrl('tag', 'read') + '&property=student_tags&' + str + '&school_year_id=' + this.schoolYear.id)
                    .then(response => {
                        this.items = this.$ecResponse(response, 'student_tags')
                    })
                    .finally(() => {
                        this.$setLoading(false)
                    })
            },
            loadDataByCategory() {
                if (!this.category.length > 0) return
                this.$setLoading(true)
                let p = {
                    category_id: [],
                    school_year_id: this.schoolYear.id
                }
                this.category.forEach(cat => {
                    p.category_id.push(cat.id)
                })
                this.$axios.get('studentInterventionPlans.php?action=get&property=interventions&'+this.$objectToParams(p))
                .then(response => {
                    this.items = this.$ecResponse(response, 'students')
                })
                .finally(() => {
                    this.$setLoading(false)
                })
            },
        }
    }
</script>

<style lang="scss" scoped>
    .student-search-option {
        cursor: pointer;
        height: 40px;
        align-items: center;
        display: flex;
        padding: 10px;

        &.selected-student {
            border-left: 4px solid #005673 !important;
            border-radius: 4px;
            background: #E5F0F4;
        }
    }
</style>
